import React, { useEffect, useMemo, useState } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  XGBoostDecision,
  XGBoostDecisionsParams,
  getXGBoostDecisions,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface XGBoostSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const XGBoostSection: React.FC<XGBoostSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const [showPassed, setShowPassed] = useState<string>('all');
  const [xgboostEnabled, setXgboostEnabled] = useState<boolean | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function checkEnabled() {
      try {
        const resp = await fetch('/api/admin/xgboost/overview', { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          const enabled = data?.enabled ?? true;
          if (!cancelled) setXgboostEnabled(enabled);
        } else {
          if (!cancelled) setXgboostEnabled(true);
        }
      } catch {
        if (!cancelled) setXgboostEnabled(true);
      } finally {
        if (!cancelled) setSettingsLoaded(true);
      }
    }
    checkEnabled();
    return () => { cancelled = true; };
  }, []);

  const params = useMemo<XGBoostDecisionsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
    passed: showPassed === 'all' ? undefined : showPassed === 'passed',
  }), [dateFrom, dateTo, symbol, showPassed]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } =
    useFlowData<XGBoostDecision, XGBoostDecisionsParams>({
      queryKeyBase: ['admin', 'trade-flow', 'xgboost'],
      fetchFn: (p) => getXGBoostDecisions(p).then((r) => r),
      params,
      refetchInterval: 10000,
    });

  if (!settingsLoaded) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>XGBoost AI Filter</h2>
          <span className={styles.sectionBadge}>Tab 4</span>
        </div>
        <div className={styles.loadingState}>Checking XGBoost status...</div>
      </div>
    );
  }

  if (xgboostEnabled === false) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2>XGBoost AI Filter</h2>
          <span className={styles.sectionBadge}>Tab 4</span>
          <span className={styles.disabledBadge}>(Off)</span>
        </div>
        <p className={styles.sectionDescription}>
          Machine learning model decisions. Each pair is scored by the XGBoost model against a configurable threshold.
        </p>
        <div className={styles.disabledBanner}>
          XGBoost AI Filter is currently disabled. Pipeline filters (Tab 3) are handling pair selection instead.
          To enable XGBoost, toggle the setting in the Settings Panel or under Feature Flags.
        </div>
      </div>
    );
  }

  return (
    <DataSection
      title="XGBoost AI Filter"
      tabNumber={4}
      description="Machine learning model decisions. Each pair is scored by the XGBoost model against a configurable threshold."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <div className={styles.filterBar}>
        <select
          value={showPassed}
          onChange={(e) => { setShowPassed(e.target.value); setPage(1); }}
          className={styles.filterSelect}
        >
          <option value="all">All Results</option>
          <option value="passed">Passed Only</option>
          <option value="failed">Failed Only</option>
        </select>
      </div>

      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Stage</th>
            <th>Score</th>
            <th>Threshold</th>
            <th>Result</th>
            <th>Model</th>
            <th>Reason</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.id}>
              <td className={styles.monoSmall}>
                {d.created_at ? new Date(d.created_at).toLocaleString() : '-'}
              </td>
              <td className={styles.mono}>{d.symbol}</td>
              <td>{d.stage || '-'}</td>
              <td className={styles.mono}>
                {d.score !== null ? d.score.toFixed(4) : '-'}
              </td>
              <td className={styles.mono}>
                {d.threshold !== null ? d.threshold.toFixed(4) : '-'}
              </td>
              <td>
                <span className={d.passed ? styles.badgePass : styles.badgeFail}>
                  {d.passed ? 'PASS' : 'FAIL'}
                </span>
              </td>
              <td className={styles.monoSmall}>{d.model_version || '-'}</td>
              <td className={styles.detailCell}>{d.reason || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataSection>
  );
};

export default XGBoostSection;
